// Anita Week 1 / Day 5 — Week Project: Anita's Little Shop
// (U.S. Grade 3 math + project upgrade).
// Project day: base 5 / max 8 nims. Validator requires parentGuide.rubric and
// the reading-review-words / oral-defense-reading task ids on Day 5.
// Math is Grade 3: equal groups across product lines, total cost of materials
// + net value, simple fractions 1/2 and 1/4, perimeter of a rectangle, and a
// completion-of-a-table review. Russian stays at RU Grade 1–2.
// SAFETY: paper simulations only — Anita's shop is a paper shop. No real selling.

const day5 = {
  id: 'day-5',
  studentId: 'anita',
  weekId: 'week-1',
  dayId: 'day-5',
  status: 'active',
  title: 'Проект недели: маленькая лавка Аниты / Week Project: Anita\'s Little Shop',
  shortTitle: 'Маленькая лавка / Little Shop',
  theme:
    'Анита собирает идеи недели в один маленький бумажный проект — лавку. Считает товары, материалы, чистую ценность и объясняет выбор. / Anita combines the week into one small paper shop. She counts items, materials, net value and explains her choice.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'project', 'spelling', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни и Анита открывают маленькую бумажную лавку. Это итоговый проект первой недели: товары, материалы, чистая ценность, вывеска и устная защита. / Nimny and Anita open a small paper shop. This is the Week 1 capstone: products, materials, net value, sign and an oral defense.',
  goals: [
    'Собрать товары лавки: 6 карточек × 5, 4 закладки × 8, 3 наклейки × 6 нимов. / Build shop products with equal-group multiplication.',
    'Посчитать стоимость материалов и чистую ценность после материалов. / Find the material cost and net value.',
    'Работать с дробями 1/2 и 1/4 от 24 карточек. / Use 1/2 and 1/4 of 24 cards.',
    'Найти периметр вывески 12 × 7 см. / Find the perimeter of a 12 × 7 cm sign.',
    'Заполнить таблицу проекта и провести устную защиту по-русски. / Complete the project table and present an oral defense in Russian.'
  ],
  vocabulary: [
    { term: 'лавка / shop', definition: 'маленький бумажный магазин для проекта. / a small paper shop for the project.' },
    { term: 'товар / item', definition: 'вещь, которая стоит в лавке. / a thing on the shop shelf.' },
    { term: 'материалы / materials', definition: 'бумага, наклейки и ленточка для товаров. / paper, stickers and ribbon for items.' },
    { term: 'ценность / value', definition: 'сколько нимов стоит вещь в игре. / how many nims a thing is worth in the game.' },
    { term: 'чистая ценность / net value', definition: 'ценность товаров минус материалы. / item value minus materials.' },
    { term: 'вывеска / sign', definition: 'табличка с названием лавки. / a sign with the shop name.' },
    { term: 'периметр / perimeter', definition: 'длина всех сторон фигуры. / the total of all sides.' },
    { term: 'выбор / choice', definition: 'что я делаю снова, а что меняю. / what I keep and what I change.' }
  ],
  onlineTasks: [
    {
      id: 'reading-review-words',
      type: 'text',
      title: 'Слова недели / Words of the Week',
      prompt: 'Прочитай: деньги, цена, остаток, набор, чистая ценность, копилка, цель, лавка, товар, материалы, периметр.',
      ruPrimaryText: 'Прочитай по-русски слова недели и проекта.',
      enHelpText: 'Read in Russian: money, price, remainder, set, net value, savings jar, goal, shop, item, materials, perimeter.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения русских слов недели и проекта. / Attempted Russian reading.'
    },
    {
      id: 'oral-defense-reading',
      type: 'text',
      title: 'Ключевые фразы — защита проекта / Key Phrases — Project Defense',
      prompt:
        'Прочитай и попробуй сказать:\n1. Это моя маленькая лавка.\n2. В лавке есть карточки, закладки и наклейки.\n3. Я посчитала чистую ценность после материалов.\n4. Половину карточек я оставляю для игры.\n5. Самый умный выбор в лавке — это ______, потому что ______.',
      ruPrimaryText:
        'Прочитай и скажи 5 ключевых фраз. Это будет твоя устная защита проекта.',
      enHelpText:
        '1. This is my little shop.\n2. The shop has cards, bookmarks and stickers.\n3. I counted the net value after materials.\n4. I keep half of the cards for the game.\n5. The smartest choice in the shop is ___, because ___.',
      answer: 'Попытка чтения и устной речи 5 фраз по-русски.'
    },
    {
      id: 'math-1-shop-items-value',
      type: 'math',
      title: 'Math 1 — Товары лавки / Shop Items',
      prompt:
        'Анита готовит маленькую лавку.\n6 карточек по 5 нимов.\n4 закладки по 8 нимов.\n3 наклейки по 6 нимов.\n\nКакая общая игровая ценность всех вещей?',
      ruPrimaryText:
        '6 × 5 + 4 × 8 + 3 × 6 = ?',
      enHelpText:
        '6 cards at 5 nims; 4 bookmarks at 8 nims; 3 stickers at 6 nims. What is the total game value?',
      helper: 'Перемножь каждую группу, потом сложи. / Multiply each group, then sum.',
      answer: '80',
      unit: 'нимов ценности / nims of value'
    },
    {
      id: 'math-2-materials-net-value',
      type: 'math',
      title: 'Math 2 — Материалы и чистая ценность / Materials and Net Value',
      prompt:
        'Материалы стоили:\nбумага 18 нимов,\nнаклейки 12 нимов,\nленточка 9 нимов.\n\nСколько стоили материалы?\nКакая чистая ценность осталась после материалов, если общая ценность вещей 80?\n\nЗапиши ответ через /: материалы / чистая ценность. Например: 39 / 41.',
      ruPrimaryText:
        '18 + 12 + 9 = ?. 80 − ? = ?. Ответ через /: 39 / 41.',
      enHelpText:
        'Materials: paper 18, stickers 12, ribbon 9. Find the material total. Then 80 − materials = net value. Write the answer with a slash: materials / net value. Example: 39 / 41.',
      helper: 'Сначала сумма материалов, потом 80 минус сумма. Ответ через /: материалы / чистая ценность. / Sum first, then subtract. Format as materials / net value.',
      answer: '39 / 41',
      unit: 'нимов / nims'
    },
    {
      id: 'math-3-half-quarter-shop',
      type: 'math',
      title: 'Math 3 — Половина и четверть / Half and a Quarter',
      prompt:
        'Из 24 карточек Анита:\n1/2 оставила для игры,\n1/4 подарила,\nостальные положила в лавку.\n\nСколько карточек пошло в лавку?',
      ruPrimaryText:
        '24 карточки. 1/2 себе, 1/4 подарила, остальное — в лавку. Сколько в лавке?',
      enHelpText:
        '24 cards. 1/2 kept; 1/4 given as a gift; the rest go to the shop. How many cards go to the shop?',
      helper: '1/2 = 12; 1/4 = 6; остальное = 24 − 12 − 6. / 1/2 = 12; 1/4 = 6; rest = 24 − 12 − 6.',
      answer: '6',
      unit: 'карточек в лавку / cards to the shop'
    },
    {
      id: 'math-4-sign-perimeter',
      type: 'math',
      title: 'Math 4 — Периметр вывески / Sign Perimeter',
      prompt:
        'Анита рисует прямоугольную вывеску лавки.\nДлина 12 см, ширина 7 см.\nНайди периметр.',
      ruPrimaryText:
        'Периметр прямоугольника 12 см × 7 см = ?',
      enHelpText:
        'Anita draws a rectangular sign 12 cm by 7 cm. Find the perimeter.',
      helper: 'Периметр = 2 × (длина + ширина). / Perimeter = 2 × (length + width).',
      answer: '38',
      unit: 'см / cm'
    },
    {
      id: 'math-5-project-table',
      type: 'math',
      title: 'Math 5 — Таблица проекта / Project Table',
      prompt:
        'Заполни таблицу проекта (3 строки):\nВещь | Количество | Ценность одной | Общая ценность\nКарточки | 6 | 5 | ___\nЗакладки | 4 | 8 | ___\nНаклейки | 3 | 6 | ___\nВсего: ___\n\nЗапиши ответ через /: карточки / закладки / наклейки / всего. Например: 30 / 32 / 18 / 80.',
      ruPrimaryText:
        'Заполни таблицу: 6 × 5 = ?, 4 × 8 = ?, 3 × 6 = ?, всего = ?. Ответ через /: 30 / 32 / 18 / 80.',
      enHelpText:
        'Fill in the project table: 6 × 5, 4 × 8, 3 × 6, then the total. Write the answer with slashes: cards / bookmarks / stickers / total. Example: 30 / 32 / 18 / 80.',
      helper: 'Перемножь каждую строку, потом сложи столбец «Общая ценность». Ответ через /: карточки / закладки / наклейки / всего. / Multiply each row, then sum. Format with slashes.',
      answer: '30 / 32 / 18 / 80',
      unit: 'нимов / nims'
    },
    {
      id: 'logic-shop-choice',
      type: 'text',
      title: 'Логика — какой товар сделать снова? / Logic — Which Item to Repeat?',
      prompt:
        'Выбери одну вещь, которую стоит сделать ещё раз для лавки.\nОбъясни почему:\n— легко сделать,\n— полезно,\n— хорошая ценность,\n— нравится покупателю.\n1–2 предложения по-русски.',
      ruPrimaryText:
        'Выбери одну вещь и объясни, почему её стоит сделать снова.',
      enHelpText:
        'Pick one item worth making again. Explain why in 1–2 Russian sentences.',
      placeholder: 'Я бы сделала ещё ___, потому что ...',
      answer:
        'Принимается любой выбор с осмысленным объяснением (легко сделать, полезно, хорошая ценность, нравится покупателю).'
    },
    {
      id: 'spelling-review',
      type: 'text',
      title: 'Правописание недели / Weekly Spelling',
      prompt:
        'Напиши 5 любых слов из списка:\nденьги, цена, остаток, набор, чистая ценность, копилка, цель, лавка, товар, материалы, периметр.',
      ruPrimaryText: 'Напиши 5 любых слов из списка недели.',
      enHelpText: 'Write any 5 words from the weekly list (see above).',
      answer: 'Любые 5 слов с попыткой правильного написания.'
    },
    {
      id: 'project-shop',
      type: 'text',
      title: 'Проект «Маленькая лавка» / Project: Little Shop',
      prompt:
        'Создай проект «Маленькая лавка».\nВ проекте должно быть:\n1. Название лавки.\n2. Заполненная таблица проекта (3 строки + всего).\n3. Чистая ценность после материалов.\n4. Рисунок вывески 12 × 7 см с подписью.\n5. 3 короткие фразы для устной защиты:\n   «Моя лавка называется …»,\n   «Я сделала …»,\n   «Самый умный выбор — …»',
      ruPrimaryText:
        'Создай проект «Маленькая лавка»: название, таблица, чистая ценность, вывеска 12 × 7, 3 фразы для защиты.',
      enHelpText:
        'Create the "Little Shop" project: name, filled table, net value, 12 × 7 cm sign, and 3 oral-defense sentences.',
      answer: 'Open-ended project. Должны быть все 5 секций и устная защита родителю.'
    },
    {
      id: 'speech-week-summary',
      type: 'text',
      title: 'Расскажи родителю / Tell Your Parent',
      prompt: 'Скажи по-русски: «Моя лавка называется …. Самый умный выбор — …, потому что ….»',
      ruPrimaryText: 'Скажи по-русски минимум 2 фразы про лавку и про умный выбор.',
      enHelpText: 'Say in Russian at least 2 sentences about the shop and the smartest choice.',
      answer:
        'Принимаются 2 русские фразы с названием лавки и объяснением выбора.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши название лавки.',
    '2. Заполни таблицу проекта (3 строки + всего) в тетради.',
    '3. Реши Math 1–5 с шагами.',
    '4. Нарисуй вывеску 12 × 7 см и найди периметр.',
    '5. Напиши 5 слов недели.',
    '6. Подготовь и скажи родителю 3 фразы для защиты проекта.'
  ],
  printWorksheet: {
    title: 'Маленькая лавка Аниты / Anita\'s Little Shop',
    subtitle: 'Неделя 1 · День 5 — проект / Week 1 · Day 5 — Project',
    pages: [
      {
        title: 'Слова и фразы недели / Weekly Words and Phrases',
        blocks: [
          { type: 'text', text: 'Название проекта: «Маленькая лавка». / Project name: "Little Shop".' },
          { type: 'list', items: [
            'Слова недели: деньги, цена, остаток, набор, чистая ценность, копилка, цель, лавка, товар, материалы, периметр. / Weekly words.',
            'Чтение 5 ключевых фраз для устной защиты (см. экран). / 5 key phrases for the oral defense.',
            'English help мелким шрифтом. / English help in small print.'
          ] }
        ]
      },
      {
        title: 'Математическая проверка / Math Review',
        blocks: [
          { type: 'text', text: 'Показывай шаги и единицы. / Show steps and units.' },
          { type: 'list', items: [
            'Math 1. 6 × 5 + 4 × 8 + 3 × 6 = ___ нимов общей ценности. / Total item value.',
            'Math 2. Материалы 18 + 12 + 9 = ___. Чистая ценность 80 − ___ = ___. / Materials and net value.',
            'Math 3. 1/2 от 24 = ___; 1/4 от 24 = ___; в лавку = 24 − ___ − ___ = ___ карточек. / Half and a quarter.',
            'Math 4. Вывеска 12 × 7 см. Периметр = 2 × (12 + 7) = ___ см. / Sign perimeter.',
            'Math 5. Таблица: 6 × 5 = ___, 4 × 8 = ___, 3 × 6 = ___, всего ___. / Project table.'
          ] }
        ]
      },
      {
        title: 'Проект и защита / Project and Defense',
        blocks: [
          { type: 'list', items: [
            'Название моей лавки: __________________________ . / Shop name.',
            'Таблица проекта: 3 строки (Карточки 6×5, Закладки 4×8, Наклейки 3×6) + Всего. / Project table with 3 rows + total.',
            'Чистая ценность после материалов: 80 − 39 = ___ нимов. / Net value.',
            'Нарисуй вывеску 12 × 7 см и подпиши её. Периметр = ___ см. / Draw the 12 × 7 cm sign with perimeter.',
            'Логика: «Я бы сделала ещё ___, потому что ___ .» 1–2 предложения. / Logic — which item to repeat and why.',
            '3 фразы для устной защиты: «Моя лавка называется …», «Я сделала …», «Самый умный выбор — …». / 3 oral-defense sentences.',
            'Parent rubric (см. parentGuide.rubric). / Parent rubric.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Анита собирает идеи недели в один маленький бумажный проект. Считает товары, материалы, чистую ценность, дроби, периметр; заполняет таблицу проекта и проводит устную защиту по-русски.',
    mathGoal:
      'U.S. Grade 3 review: × как первый шаг (6 × 5, 4 × 8, 3 × 6), суммирование результатов, материалы 18 + 12 + 9 и чистая ценность, простые дроби 1/2 и 1/4 от 24, периметр прямоугольника 12 × 7, таблица проекта.',
    russianGoal:
      'Прочитать слова недели и 5 ключевых фраз для защиты проекта.',
    readingWritingGoal:
      'Прочитать 5 фраз, написать 5 слов, заполнить таблицу проекта.',
    practicalGoal:
      'Создать проект «Маленькая лавка» и провести устную защиту.',
    whatThisLessonTeaches:
      'Анита переводит знания недели в проект: считает группами, понимает «чистую ценность», умеет работать с дробями и периметром, объясняет, почему выбрала именно эти товары.',
    keyMessage:
      'Лавка — это план + материалы + товары + объяснение выбора. Цена в нимах — учебная симуляция, не настоящие деньги.',
    howToExplain:
      'Попросите Аниту провести «экскурсию» по проекту: «Вот товары, вот материалы, вот чистая ценность, вот вывеска, вот мой умный выбор».',
    exampleDialogue:
      'Parent: Какая общая ценность твоих товаров?\nChild: 30 + 32 + 18 = 80.\nParent: А чистая ценность после материалов?\nChild: 80 − 39 = 41.\nParent: Какой товар ты сделаешь снова и почему?\nChild: Закладки, потому что 4 × 8 = 32 — много ценности за немного времени.',
    readingSupport:
      'Анита может читать фразы по одной. Родитель помогает со сложными словами: «чистая ценность», «периметр», «защита».',
    russianSpeechGoal:
      'Анита говорит минимум 3 русские фразы из устной защиты, включая «Самый умный выбор — …».',
    mathAnswerKey:
      'Math 1: 6 × 5 = 30; 4 × 8 = 32; 3 × 6 = 18; всего 80 нимов.\nMath 2: материалы 18 + 12 + 9 = 39; чистая ценность 80 − 39 = 41.\nMath 3: 1/2 от 24 = 12; 1/4 от 24 = 6; в лавку 24 − 12 − 6 = 6 карточек.\nMath 4: периметр 2 × (12 + 7) = 38 см.\nMath 5: 30, 32, 18; всего 80.',
    spellingAnswerKey:
      'Принимаются любые 5 слов из списка. Правильные формы:\nденьги\nцена\nостаток\nнабор\nчистая ценность\nкопилка\nцель\nлавка\nтовар\nматериалы\nпериметр',
    answerKey: [
      { taskId: 'reading-review-words', answer: 'Answer: попытка чтения слов недели и проекта.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'oral-defense-reading', answer: 'Answer: попытка чтения и устной речи 5 фраз.\nSolution: Каждая фраза — отдельно.\nParent note: Главное — мысль, не идеальная фонетика.' },
      { taskId: 'math-1-shop-items-value', answer: 'Answer: 80 нимов общей ценности.\nSolution: 6 × 5 = 30; 4 × 8 = 32; 3 × 6 = 18; 30 + 32 + 18 = 80.\nParent note: Это три равно-групповых × + суммирование.' },
      { taskId: 'math-2-materials-net-value', answer: 'Answer: материалы 39, чистая ценность 41.\nSolution: 18 + 12 + 9 = 39; 80 − 39 = 41.\nParent note: Связывайте «чистая ценность» с «после материалов».' },
      { taskId: 'math-3-half-quarter-shop', answer: 'Answer: 6 карточек в лавку.\nSolution: 1/2 от 24 = 12; 1/4 от 24 = 6; 24 − 12 − 6 = 6.\nParent note: 1/2 + 1/4 = 3/4, поэтому остаётся 1/4 = 6.' },
      { taskId: 'math-4-sign-perimeter', answer: 'Answer: 38 см.\nSolution: 2 × (12 + 7) = 2 × 19 = 38.\nParent note: Можно также сложить все 4 стороны: 12 + 7 + 12 + 7 = 38.' },
      { taskId: 'math-5-project-table', answer: 'Answer: 30, 32, 18; всего 80.\nSolution: Перемножь каждую строку и сложи столбец общей ценности.\nParent note: Это проверка Math 1 в табличной форме.' },
      { taskId: 'logic-shop-choice', answer: 'Answer: любой обоснованный выбор.\nSolution: Принимается «закладки — много ценности», «карточки — легко делать», «наклейки — нравится покупателю».\nParent note: Главное — объяснение, а не сам выбор.' },
      { taskId: 'spelling-review', answer: 'Answer: любые 5 слов из списка.\nSolution: Принимаются любые написания, если слово узнаётся.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'project-shop', answer: 'Answer: проект «Маленькая лавка» из 5 секций.\nSolution: Название + таблица + чистая ценность + вывеска + 3 фразы.\nParent note: Если одна секция слабая — отметьте в рубрике, но не наказывайте.' },
      { taskId: 'speech-week-summary', answer: 'Answer: 2+ русские фразы про лавку и умный выбор.\nSolution: «Моя лавка называется … Самый умный выбор — …, потому что …».\nParent note: Главное — мысль, не идеальная грамматика.' }
    ],
    check: [
      'Анита прочитала слова недели и 5 ключевых фраз.',
      'Анита решила Math 1–5 с показом шагов.',
      'Анита создала проект «Маленькая лавка» (5 секций).',
      'Анита провела устную защиту минимум 3 фразами по-русски.',
      'Родитель проверил проект по рубрике.'
    ],
    spellingCheck: 'Проверить 5 выбранных слов. Исправлять мягко.',
    readingCheck: 'Главное — попытка русского чтения и понимание смысла.',
    practicalCheck:
      'Проект показывает товары, материалы, чистую ценность, вывеску и умный выбор.',
    drawingCriteria:
      'Вывеска прямоугольная 12 × 7 см, есть подпись и подсчитанный периметр.',
    projectCriteria: [
      'Название лавки',
      'Заполненная таблица проекта (3 строки + всего)',
      'Чистая ценность после материалов',
      'Вывеска 12 × 7 см и периметр',
      '3 фразы устной защиты по-русски'
    ],
    rubric: [
      'Русское чтение и устная защита — 20 баллов / Russian reading and oral defense — 20 points',
      'Математика (Math 1–5 с шагами) — 30 баллов / Math correctness with steps — 30 points',
      'Чистая ценность после материалов — 10 баллов / Net value after materials — 10 points',
      'Дроби 1/2 и 1/4 — 5 баллов / Fractions 1/2 and 1/4 — 5 points',
      'Периметр вывески 12 × 7 — 5 баллов / Sign perimeter — 5 points',
      'Аккуратность проекта и таблицы — 15 баллов / Project and table neatness — 15 points',
      'Объяснение умного выбора — 10 баллов / Smart-choice explanation — 10 points',
      'Старательность и фокус — 5 баллов / Effort and focus — 5 points'
    ],
    whatThisLessonTeaches:
      'Анита учится переводить отдельные навыки недели в один цельный проект: посчитать, заполнить таблицу, найти периметр, объяснить выбор.',
    keyMessage:
      'Маленькая лавка — это бумажная симуляция: товары, материалы, чистая ценность и умный выбор. Это не настоящая продажа.',
    howToExplain:
      'Возьмите чистый лист, вместе нарисуйте вывеску 12 × 7 см и заполните таблицу проекта.',
    exampleDialogue:
      'Parent: Какой товар ты бы сделала снова?\nChild: Закладки.\nParent: Почему?\nChild: 4 × 8 = 32 — много ценности, и их легко делать.',
    readingSupport:
      'Длинные слова «чистая ценность», «периметр», «защита» можно прочитать вместе.',
    russianSpeechGoal:
      'Анита говорит минимум 3 русские фразы из устной защиты.',
    commonMistakes: [
      '«Чистая ценность = вся ценность.» Правильно: вся ценность минус материалы.',
      '«Периметр = площадь.» Правильно: периметр — это сумма всех сторон.',
      '«Умный выбор = самый дорогой товар.» Правильно: умный выбор объясняется (легко, полезно, много ценности).'
    ],
    discussionPrompts: [
      'Как ты считала чистую ценность?',
      'Какой товар труднее всего сделать?',
      'Какой товар приносит больше ценности за время?'
    ],
    ifEasy:
      'Усложните: пусть Анита посчитает «чистую ценность за один товар» по каждой строке: карточки 30 − 8 материалов = 22, закладки 32 − 18 = 14, наклейки 18 − 13 = 5. Какой ряд даёт самую большую чистую ценность за один товар? (Закладки: 14 ÷ 4 = 3.5 нима за закладку; карточки: 22 ÷ 6 ≈ 3.7.)',
    ifDifficult:
      'Оставьте только Math 1, Math 2 и проект-таблицу. Периметр сделайте вместе.',
    rewardGuidance:
      'Проектный день: базовая награда 5 нимов. Бонусы за устную защиту, правильную чистую ценность и аккуратную таблицу.',
    parentOnlyNotes:
      'Не превращайте лавку в реальную продажу. Не используйте слова «зарплата», «оплата», «прибыль». Цена в нимах — учебная симуляция.',
    tokenReward:
      'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 5,
    max: 8,
    label: 'Проектная награда / Project Reward',
    display: '5 нимов',
    bonuses: [
      { id: 'good-russian-defense', label: 'Бонус за хорошую устную защиту по-русски', amount: 1 },
      { id: 'correct-net-value', label: 'Бонус за правильную чистую ценность', amount: 1 },
      { id: 'neat-project-table', label: 'Бонус за аккуратную таблицу проекта', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-review-words',
      'oral-defense-reading',
      'math-1-shop-items-value',
      'math-2-materials-net-value',
      'math-3-half-quarter-shop',
      'math-4-sign-perimeter',
      'math-5-project-table',
      'logic-shop-choice',
      'spelling-review',
      'project-shop',
      'speech-week-summary'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-2-day-1'
};

export default day5;
